import React, { useState, useEffect } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';

// Registrar componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

// Definir cores
const colors = {
  primary: '#6695ED', // Azul pastel
  primaryLight: 'rgba(102, 149, 237, 0.1)',
  primaryDark: '#4a77d4',
  success: '#66CDAA', // Verde pastel
  successLight: 'rgba(102, 205, 170, 0.1)',
  successDark: '#4daf8c',
  danger: '#FFA07A', // Salmão claro
  dangerLight: 'rgba(255, 160, 122, 0.1)',
  dangerDark: '#ff8266',
  warning: '#FFD580', // Amarelo pastel
  warningLight: 'rgba(255, 213, 128, 0.1)',
  warningDark: '#ffc966',
  info: '#87CEFA', // Azul céu claro
  infoLight: 'rgba(135, 206, 250, 0.1)',
  infoDark: '#6ec2f7',
  purple: '#B19CD9', // Lavanda
  purpleLight: 'rgba(177, 156, 217, 0.1)',
  purpleDark: '#9a81cc',
  pink: '#FFB6C1', // Rosa claro
  pinkLight: 'rgba(255, 182, 193, 0.1)',
  pinkDark: '#ff9aad',
  dark: '#4A5568', // Cinza escuro mais suave
  darkLight: 'rgba(74, 85, 104, 0.1)',
  gray: '#A0AEC0',
  grayLight: '#F7FAFC',
  text: '#4A5568',
  border: '#E2E8F0'
};

const TesteDesign = () => {
  const [groupingType, setGroupingType] = useState('daily');
  const [dateRange, setDateRange] = useState({
    start: '2025-02-15',
    end: '2025-03-15'
  });

  // Dados simulados para teste
  const simulationDetails = {
    data: {
      initialAmountUSD: 1365.00,
      finalAmountUSD: 100982.19,
      totalProfit: 99617.19,
      totalProfitPercentage: 7297.96,
      monthsToSimulate: 12
    },
    simulation: {
      id: 1,
      start_date: '2025-03-15T00:00:00.000Z',
      end_date: '2026-03-15T00:00:00.000Z',
      InvestmentSetting: {
        entry_fee: 0.001,
        exit_fee: 0.001,
        profit_rate: 0.008,
        daily_withdrawal_rate: 0.002,
        operations_per_day: 3
      }
    },
    daily_operations: generateDailyOperations(),
    operations: generateOperations()
  };

  // Gerar dados para os gráficos
  const [chartData, setChartData] = useState({
    monthly: { labels: [], datasets: [] },
    cumulative: { labels: [], datasets: [] }
  });

  useEffect(() => {
    generateChartData();
  }, []);

  function generateDailyOperations() {
    const operations = [];
    const startDate = new Date('2025-03-15');
    let amount = 1365.00;

    for (let i = 1; i <= 30; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i - 1);
      
      const profit = amount * 0.024; // 0.8% * 3 operações
      const fees = amount * 0.006; // 0.1% * 2 * 3 operações
      const withdrawal = profit * 0.002;
      
      operations.push({
        id: i,
        day_number: i,
        date: date.toISOString(),
        initial_amount_usd: amount,
        final_amount_usd: amount + profit - fees - withdrawal,
        total_profit: profit,
        total_fees: fees,
        daily_withdrawal: withdrawal
      });
      
      amount = amount + profit - fees - withdrawal;
    }
    
    return operations;
  }

  function generateOperations() {
    return [];
  }

  function generateChartData() {
    // Dados para o gráfico mensal
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const monthlyProfits = [8301.43, 9254.67, 11325.89, 14589.32, 17895.64, 22301.45, 25478.90, 32654.78, 37895.41, 42658.93, 47895.32, 52658.93];
    
    // Dados para o gráfico de evolução
    const cumulativeData = [];
    let cumulativeTotal = 1365.00;
    
    for (let i = 0; i < 12; i++) {
      cumulativeTotal += monthlyProfits[i];
      cumulativeData.push(cumulativeTotal);
    }

    // Configurar dados dos gráficos
    setChartData({
      monthly: {
        labels: monthNames,
        datasets: [
          {
            label: 'Lucro Mensal',
            data: monthlyProfits,
            backgroundColor: colors.primary,
            borderColor: colors.primary,
            borderWidth: 1
          }
        ]
      },
      cumulative: {
        labels: monthNames,
        datasets: [
          {
            label: 'Saldo Total',
            data: cumulativeData,
            borderColor: colors.primary,
            backgroundColor: 'rgba(102, 149, 237, 0.1)',
            tension: 0.4,
            fill: false
          },
          {
            label: 'Valor Investido',
            data: Array(12).fill(1365.00),
            borderColor: colors.gray,
            backgroundColor: 'rgba(150, 160, 170, 0.1)',
            tension: 0.4,
            fill: false
          },
          {
            label: 'Lucro Acumulado',
            data: cumulativeData.map(val => val - 1365.00),
            borderColor: colors.success,
            backgroundColor: 'rgba(102, 205, 170, 0.1)',
            tension: 0.4,
            fill: false
          }
        ]
      }
    });
  }

  // Estilo personalizado para os gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          usePointStyle: true,
          boxWidth: 6
        }
      },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: colors.dark,
        bodyColor: colors.text,
        borderColor: colors.border,
        borderWidth: 1,
        padding: 16,
        cornerRadius: 8,
        displayColors: false,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: '600'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: colors.gray
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          borderDash: [2, 2],
          color: colors.grayLight
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: colors.gray
        }
      }
    }
  };

  // Funções de formatação
  const formatCurrency = (value, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency === 'USD' ? 'USD' : 'BRL',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  return (
    <MainLayout>
      <div className="analytics-dashboard" style={{ fontFamily: "'Inter', sans-serif", color: colors.text, backgroundColor: '#F9FBFD', minHeight: '100vh', padding: '20px' }}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.dark, margin: 0 }}>Análise de Investimentos</h1>
            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
              Visualize o desempenho das suas simulações de investimento
            </p>
          </div>
          
          <div className="d-flex align-items-center">
            {/* Seletor de período */}
            <div className="date-range-selector d-flex align-items-center me-3" style={{ backgroundColor: 'white', borderRadius: '8px', padding: '8px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: `1px solid ${colors.border}` }}>
              <input 
                type="date" 
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                style={{ border: 'none', fontSize: '0.875rem', color: colors.text }}
              />
              <span className="mx-2" style={{ color: colors.gray }}>até</span>
              <input 
                type="date" 
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                style={{ border: 'none', fontSize: '0.875rem', color: colors.text }}
              />
            </div>

            {/* Seletor de simulação */}
            <div className="d-flex">
              <select 
                className="form-select me-2"
                style={{ 
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  width: '220px'
                }}
              >
                <option value="1">Simulação #1 - 15/03/2025</option>
                <option value="2">Simulação #2 - 10/03/2025</option>
                <option value="3">Simulação #3 - 05/03/2025</option>
              </select>
              
              <button 
                style={{ 
                  backgroundColor: colors.primary, 
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontWeight: 500,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                Atualizar
              </button>
            </div>
          </div>
        </div>
        
        {/* Principais métricas - cards com cores pastel e borda lateral */}
        <div className="row g-3 mb-4">
          {/* Card 1: Valor Inicial - Azul Pastel */}
          <div className="col-md-3">
            <div className="metric-card h-100" style={{ 
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '20px 20px 20px 24px', 
              boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Borda lateral decorativa */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: colors.primary,
                borderRadius: '6px 0 0 6px'
              }}></div>
              
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 500, color: colors.gray, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Valor Inicial</span>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: 600, color: colors.dark, marginTop: '12px', marginBottom: '8px' }}>
                    {formatCurrency(simulationDetails.data.initialAmountUSD)}
                  </h2>
                  <div className="metric-footer">
                    <span style={{ fontSize: '0.75rem', color: colors.gray }}>
                      Data de Início: {formatDate(simulationDetails.simulation.start_date)}
                    </span>
                  </div>
                </div>
                <div className="icon-box" style={{ 
                  backgroundColor: colors.primaryLight, 
                  borderRadius: '12px', 
                  width: '42px', 
                  height: '42px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 2: Valor Final - Verde Pastel */}
          <div className="col-md-3">
            <div className="metric-card h-100" style={{ 
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '20px 20px 20px 24px', 
              boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Borda lateral decorativa */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: colors.success,
                borderRadius: '6px 0 0 6px'
              }}></div>
              
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 500, color: colors.gray, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Valor Final</span>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: 600, color: colors.dark, marginTop: '12px', marginBottom: '8px' }}>
                    {formatCurrency(simulationDetails.data.finalAmountUSD)}
                  </h2>
                  <div className="metric-footer">
                    <span style={{ fontSize: '0.75rem', color: colors.gray }}>
                      Data de Término: {formatDate(simulationDetails.simulation.end_date)}
                    </span>
                  </div>
                </div>
                <div className="icon-box" style={{ 
                  backgroundColor: colors.successLight, 
                  borderRadius: '12px', 
                  width: '42px', 
                  height: '42px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 3: Lucro Total - Lavanda */}
          <div className="col-md-3">
            <div className="metric-card h-100" style={{ 
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '20px 20px 20px 24px', 
              boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Borda lateral decorativa */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: colors.purple,
                borderRadius: '6px 0 0 6px'
              }}></div>
              
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 500, color: colors.gray, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Lucro Total</span>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: 600, color: colors.dark, marginTop: '12px', marginBottom: '8px' }}>
                    {formatCurrency(simulationDetails.data.totalProfit)}
                  </h2>
                  <div className="metric-footer d-flex align-items-center">
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.purple, marginRight: '4px', backgroundColor: colors.purpleLight, padding: '2px 8px', borderRadius: '12px' }}>
                      {formatPercent(simulationDetails.data.totalProfitPercentage)}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: colors.gray }}>
                      Rentabilidade
                    </span>
                  </div>
                </div>
                <div className="icon-box" style={{ 
                  backgroundColor: colors.purpleLight, 
                  borderRadius: '12px', 
                  width: '42px', 
                  height: '42px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 4: Período - Rosa */}
          <div className="col-md-3">
            <div className="metric-card h-100" style={{ 
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '20px 20px 20px 24px', 
              boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Borda lateral decorativa */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: colors.pink,
                borderRadius: '6px 0 0 6px'
              }}></div>
              
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 500, color: colors.gray, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Período</span>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: 600, color: colors.dark, marginTop: '12px', marginBottom: '8px' }}>
                    {simulationDetails.data.monthsToSimulate} meses
                  </h2>
                  <div className="metric-footer">
                    <span style={{ fontSize: '0.75rem', color: colors.gray }}>
                      Operações: 1080
                    </span>
                  </div>
                </div>
                <div className="icon-box" style={{ 
                  backgroundColor: colors.pinkLight, 
                  borderRadius: '12px', 
                  width: '42px', 
                  height: '42px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.pink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Parâmetros da simulação - 4 cards divididos ao meio */}
        <div className="row g-3 mb-4">
          {/* Card 1: Taxa de Entrada e Taxa de Saída */}
          <div className="col-md-3">
            <div className="parameter-card h-100" style={{ 
              backgroundColor: 'white', 
              borderRadius: '12px', 
              boxShadow: '0 2px 6px rgba(0,0,0,0.04)', 
              border: `1px solid ${colors.border}`,
              overflow: 'hidden'
            }}>
              {/* Taxa de Entrada */}
              <div style={{ padding: '16px', borderBottom: `1px solid ${colors.border}` }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: '0.8125rem', color: colors.gray }}>Taxa de Entrada</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, backgroundColor: colors.primaryLight, color: colors.primary, padding: '2px 8px', borderRadius: '12px' }}>
                    {formatPercent(simulationDetails.simulation.InvestmentSetting.entry_fee * 100)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark }}>
                    {formatCurrency(simulationDetails.data.initialAmountUSD * simulationDetails.simulation.InvestmentSetting.entry_fee, 'USD')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>por operação</span>
                </div>
              </div>
              
              {/* Taxa de Saída */}
              <div style={{ padding: '16px' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: '0.8125rem', color: colors.gray }}>Taxa de Saída</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, backgroundColor: colors.dangerLight, color: colors.danger, padding: '2px 8px', borderRadius: '12px' }}>
                    {formatPercent(simulationDetails.simulation.InvestmentSetting.exit_fee * 100)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark }}>
                    {formatCurrency(simulationDetails.data.initialAmountUSD * simulationDetails.simulation.InvestmentSetting.exit_fee, 'USD')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>por operação</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 2: Taxa de Lucro e Remuneração Diária */}
          <div className="col-md-3">
            <div className="parameter-card h-100" style={{ 
              backgroundColor: 'white', 
              borderRadius: '12px', 
              boxShadow: '0 2px 6px rgba(0,0,0,0.04)', 
              border: `1px solid ${colors.border}`,
              overflow: 'hidden'
            }}>
              {/* Taxa de Lucro */}
              <div style={{ padding: '16px', borderBottom: `1px solid ${colors.border}` }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: '0.8125rem', color: colors.gray }}>Taxa de Lucro</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, backgroundColor: colors.successLight, color: colors.success, padding: '2px 8px', borderRadius: '12px' }}>
                    {formatPercent(simulationDetails.simulation.InvestmentSetting.profit_rate * 100)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark }}>
                    {formatCurrency(simulationDetails.data.initialAmountUSD * simulationDetails.simulation.InvestmentSetting.profit_rate, 'USD')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>por operação</span>
                </div>
              </div>
              
              {/* Remuneração Diária */}
              <div style={{ padding: '16px' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: '0.8125rem', color: colors.gray }}>Remuneração Diária</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, backgroundColor: colors.warningLight, color: colors.warning, padding: '2px 8px', borderRadius: '12px' }}>
                    {formatPercent(simulationDetails.simulation.InvestmentSetting.daily_withdrawal_rate * 100)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark }}>
                    {formatCurrency(simulationDetails.data.totalProfit * simulationDetails.simulation.InvestmentSetting.daily_withdrawal_rate, 'USD')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>estimado</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 3: Operações por Dia e Taxa Composta */}
          <div className="col-md-3">
            <div className="parameter-card h-100" style={{ 
              backgroundColor: 'white', 
              borderRadius: '12px', 
              boxShadow: '0 2px 6px rgba(0,0,0,0.04)', 
              border: `1px solid ${colors.border}`,
              overflow: 'hidden'
            }}>
              {/* Operações por Dia */}
              <div style={{ padding: '16px', borderBottom: `1px solid ${colors.border}` }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: '0.8125rem', color: colors.gray }}>Operações por Dia</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, backgroundColor: colors.purpleLight, color: colors.purple, padding: '2px 8px', borderRadius: '12px' }}>
                    {simulationDetails.simulation.InvestmentSetting.operations_per_day}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark }}>
                    {simulationDetails.simulation.InvestmentSetting.operations_per_day * 30}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>por mês</span>
                </div>
              </div>
              
              {/* Taxa Composta */}
              <div style={{ padding: '16px' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: '0.8125rem', color: colors.gray }}>Taxa Composta</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, backgroundColor: colors.pinkLight, color: colors.pink, padding: '2px 8px', borderRadius: '12px' }}>
                    {formatPercent(simulationDetails.simulation.InvestmentSetting.profit_rate * simulationDetails.simulation.InvestmentSetting.operations_per_day * 100)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark }}>
                    {formatCurrency(simulationDetails.data.initialAmountUSD * simulationDetails.simulation.InvestmentSetting.profit_rate * simulationDetails.simulation.InvestmentSetting.operations_per_day, 'USD')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>por dia</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 4: Tempo de Investimento e ROI */}
          <div className="col-md-3">
            <div className="parameter-card h-100" style={{ 
              backgroundColor: 'white', 
              borderRadius: '12px', 
              boxShadow: '0 2px 6px rgba(0,0,0,0.04)', 
              border: `1px solid ${colors.border}`,
              overflow: 'hidden'
            }}>
              {/* Tempo de Investimento */}
              <div style={{ padding: '16px', borderBottom: `1px solid ${colors.border}` }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: '0.8125rem', color: colors.gray }}>Tempo de Investimento</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, backgroundColor: colors.infoLight, color: colors.info, padding: '2px 8px', borderRadius: '12px' }}>
                    {simulationDetails.data.monthsToSimulate}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark }}>
                    {Math.floor(simulationDetails.data.monthsToSimulate / 12)} anos e {simulationDetails.data.monthsToSimulate % 12} meses
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>período</span>
                </div>
              </div>
              
              {/* ROI */}
              <div style={{ padding: '16px' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: '0.8125rem', color: colors.gray }}>ROI</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, backgroundColor: colors.successLight, color: colors.success, padding: '2px 8px', borderRadius: '12px' }}>
                    {formatPercent(simulationDetails.data.totalProfitPercentage / simulationDetails.data.monthsToSimulate)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark }}>
                    {formatPercent(simulationDetails.data.totalProfitPercentage / 12)}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>ao ano</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Gráficos */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="chart-card" style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: `1px solid ${colors.border}`, height: '100%' }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark, margin: 0 }}>Lucro Mensal</h3>
                <div className="chart-legend d-flex">
                  <div className="d-flex align-items-center me-3">
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colors.primary, marginRight: '6px' }}></div>
                    <span style={{ fontSize: '0.75rem', color: colors.gray }}>Lucro</span>
                  </div>
                </div>
              </div>
              <div style={{ height: '280px' }}>
                <Bar options={chartOptions} data={chartData.monthly} />
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="chart-card" style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: `1px solid ${colors.border}`, height: '100%' }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark, margin: 0 }}>Evolução do Investimento</h3>
                <div className="chart-legend d-flex">
                  <div className="d-flex align-items-center me-3">
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colors.primary, marginRight: '6px' }}></div>
                    <span style={{ fontSize: '0.75rem', color: colors.gray }}>Saldo</span>
                  </div>
                  <div className="d-flex align-items-center me-3">
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colors.success, marginRight: '6px' }}></div>
                    <span style={{ fontSize: '0.75rem', color: colors.gray }}>Lucro</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colors.gray, marginRight: '6px' }}></div>
                    <span style={{ fontSize: '0.75rem', color: colors.gray }}>Investido</span>
                  </div>
                </div>
              </div>
              <div style={{ height: '280px' }}>
                <Line options={chartOptions} data={chartData.cumulative} />
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabela de operações */}
        <div className="operation-results mb-4" style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}` }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: colors.dark, margin: 0 }}>Resultados da Simulação</h3>
            <div className="d-flex">
              <div className="btn-group me-2">
                {['daily', 'weekly', 'biweekly', 'monthly', 'semester', 'yearly'].map(type => (
                  <button 
                    key={type}
                    className={groupingType === type ? 'active' : ''}
                    onClick={() => setGroupingType(type)}
                    style={{ 
                      backgroundColor: groupingType === type ? colors.primary : 'white',
                      color: groupingType === type ? 'white' : colors.text,
                      border: `1px solid ${groupingType === type ? colors.primary : colors.border}`,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      padding: '6px 12px',
                      borderRadius: type === 'daily' ? '6px 0 0 6px' : type === 'yearly' ? '0 6px 6px 0' : '0',
                      boxShadow: 'none'
                    }}
                  >
                    {type === 'daily' ? 'Diário' : 
                     type === 'weekly' ? 'Semanal' : 
                     type === 'biweekly' ? 'Quinzenal' : 
                     type === 'monthly' ? 'Mensal' : 
                     type === 'semester' ? 'Semestral' : 'Anual'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="table-responsive">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ backgroundColor: colors.grayLight }}>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: colors.dark, textAlign: 'left', borderBottom: `1px solid ${colors.border}` }}>Dia</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: colors.dark, textAlign: 'left', borderBottom: `1px solid ${colors.border}` }}>Data</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: colors.dark, textAlign: 'right', borderBottom: `1px solid ${colors.border}` }}>Valor Inicial</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: colors.dark, textAlign: 'right', borderBottom: `1px solid ${colors.border}` }}>Valor Final</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: colors.dark, textAlign: 'right', borderBottom: `1px solid ${colors.border}` }}>Lucro</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: colors.dark, textAlign: 'right', borderBottom: `1px solid ${colors.border}` }}>Taxas</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: colors.dark, textAlign: 'right', borderBottom: `1px solid ${colors.border}` }}>Remuneração</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: colors.dark, textAlign: 'center', borderBottom: `1px solid ${colors.border}` }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {simulationDetails.daily_operations.slice(0, 10).map((operation) => (
                  <tr key={operation.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '14px 20px', color: colors.dark }}>{operation.day_number}</td>
                    <td style={{ padding: '14px 20px', color: colors.dark }}>{formatDate(operation.date)}</td>
                    <td style={{ padding: '14px 20px', color: colors.dark, textAlign: 'right' }}>{formatCurrency(operation.initial_amount_usd, 'USD')}</td>
                    <td style={{ padding: '14px 20px', color: colors.dark, textAlign: 'right' }}>{formatCurrency(operation.final_amount_usd, 'USD')}</td>
                    <td style={{ padding: '14px 20px', color: colors.success, fontWeight: 500, textAlign: 'right' }}>{formatCurrency(operation.total_profit, 'USD')}</td>
                    <td style={{ padding: '14px 20px', color: colors.danger, textAlign: 'right' }}>{formatCurrency(operation.total_fees, 'USD')}</td>
                    <td style={{ padding: '14px 20px', color: colors.dark, textAlign: 'right' }}>{formatCurrency(operation.daily_withdrawal, 'USD')}</td>
                    <td style={{ padding: '14px 20px', textAlign: 'center' }}>
                      <button 
                        style={{ 
                          backgroundColor: 'transparent',
                          color: colors.primary,
                          border: `1px solid ${colors.primary}`,
                          borderRadius: '4px',
                          padding: '4px 10px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center mt-3" style={{ padding: '10px 20px' }}>
              <span style={{ fontSize: '0.75rem', color: colors.gray }}>Mostrando os primeiros 10 dias de um total de 30 dias</span>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default TesteDesign; 